<script setup lang="ts">
import type { CurrencyCode, Money } from '@taler/domain'
import { Select, TextField } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AmountInput from '@/app/components/AmountInput.vue'
import type { LoanRateType, LoanType } from '../internal/types.ts'

const endDate = defineModel<string>('endDate', { required: true })
const lender = defineModel<string>('lender', { required: true })
const rateType = defineModel<LoanRateType | undefined>('rateType', { required: true })
const loanType = defineModel<LoanType | undefined>('loanType', { required: true })
const monthlyPayment = defineModel<Money | null>('monthlyPayment', { required: true })
defineProps<{ currency: CurrencyCode | undefined; locale: 'en' | 'hu' }>()

const { t } = useI18n()
const rateOptions = computed(() => [
  { value: 'FIXED' as const, label: t('loans.rateFixed') },
  { value: 'VARIABLE' as const, label: t('loans.rateVariable') },
])
const loanTypeOptions = computed(() =>
  (['MORTGAGE', 'CAR', 'STUDENT', 'PERSONAL', 'OTHER'] as const).map((value) => ({
    value,
    label: t(`loans.loanType.${value}`),
  })),
)
</script>

<template>
  <div class="grid grid-cols-2 gap-[var(--space-cell)]">
    <div class="flex flex-col gap-1">
      <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
        t('loans.loanTypeLabel')
      }}</span>
      <Select
        v-model="loanType"
        :options="loanTypeOptions"
        :placeholder="t('loans.notSet')"
        :aria-label="t('loans.loanTypeLabel')"
      />
    </div>

    <div class="flex flex-col gap-1">
      <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
        t('loans.rateType')
      }}</span>
      <Select
        v-model="rateType"
        :options="rateOptions"
        :placeholder="t('loans.rateTypeNotSet')"
        :aria-label="t('loans.rateType')"
      />
    </div>

    <label class="flex flex-col gap-1">
      <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
        t('loans.endDateOptional')
      }}</span>
      <input v-model="endDate" type="date" class="field-control" />
    </label>

    <div class="col-span-2">
      <TextField
        v-model="lender"
        :label="t('loans.lenderOptional')"
        maxlength="200"
        :placeholder="t('loans.lenderPlaceholder')"
      />
    </div>

    <div class="col-span-2 flex flex-col gap-1">
      <label
        for="loan-monthly-payment"
        class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]"
      >
        {{ t('loans.monthlyPaymentOptional') }}
      </label>
      <AmountInput id="loan-monthly-payment" v-model="monthlyPayment" :currency="currency" :locale="locale" />
    </div>
  </div>
</template>
